import Decimal from "decimal.js";
import { WorksheetCostCategory, WorksheetHeaderUnit } from "generated/graphql";
import { WorksheetCostRowInput, WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import {
  calculateEnteredCosts,
  calculateStandardRowCost,
  calculateTruckingRow,
  roundCents,
} from "utils/worksheet/worksheetMath";
import { dollarsToCents, parseDecimalInput } from "utils/worksheet/worksheetNumbers";
import { HelperResolution, resolveHelpers } from "utils/worksheet/worksheetHelpers";
import { getMaterialRowQuantity } from "utils/worksheet/worksheetRowQuantity";

export const getRowEffectiveQuantity = (
  row: WorksheetCostRowInput,
  lineQuantity: string,
  lineUnit: WorksheetHeaderUnit,
  resolution?: HelperResolution
): number => {
  const helperQuantity = resolution?.rowQuantities[row.rowUuid];
  const quantity =
    helperQuantity !== undefined
      ? helperQuantity.toNumber()
      : getMaterialRowQuantity(row, lineQuantity, lineUnit).toNumber();

  if ([WorksheetCostCategory.LABOR, WorksheetCostCategory.MATERIAL].includes(row.category)) {
    return new Decimal(quantity).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
  }

  return quantity;
};

export const getRowCost = (
  row: WorksheetCostRowInput,
  lineQuantity: string,
  lineUnit: WorksheetHeaderUnit,
  resolution?: HelperResolution
): number => {
  if (row.category === WorksheetCostCategory.TRUCKING) {
    const helperQuantity = resolution?.rowQuantities[row.rowUuid];

    if (helperQuantity !== undefined) {
      const rate = dollarsToCents(row.rate);
      const billed =
        (row.truckingUnit || "Hours") === "Hours"
          ? helperQuantity.times(resolution?.rowTrucks[row.rowUuid] ?? 0).times(rate)
          : helperQuantity.times(rate);

      return roundCents(billed);
    }

    return calculateTruckingRow({
      truckingUnit: row.truckingUnit || "Hours",
      trucksNeeded: parseDecimalInput(row.trucksNeeded),
      hoursPerTruck: parseDecimalInput(row.hoursPerTruck),
      quantity: parseDecimalInput(row.quantity),
      rate: dollarsToCents(row.rate),
    }).costTotal;
  }

  return calculateStandardRowCost(
    getRowEffectiveQuantity(row, lineQuantity, lineUnit, resolution),
    dollarsToCents(row.rate)
  );
};

export const getDirectCosts = (
  values: WorksheetItemInput,
  resolution?: HelperResolution
): number | null => {
  if (values.costRows.length === 0) {
    return null;
  }

  const helperResolution = resolution ?? resolveHelpers(values);

  return values.costRows.reduce(
    (sum, row) => sum + getRowCost(row, values.sellingQuantity, values.unit, helperResolution),
    0
  );
};

export const getEnteredCosts = (
  values: WorksheetItemInput,
  resolution?: HelperResolution
): number | null => {
  const helperResolution = resolution ?? resolveHelpers(values);

  return calculateEnteredCosts(
    values.costRows.map((row) =>
      getRowCost(row, values.sellingQuantity, values.unit, helperResolution)
    ),
    parseDecimalInput(values.overheadPercentage),
    parseDecimalInput(values.indirectPercentage)
  );
};

export const toggleRowQuantitySource = (
  current: string | null | undefined,
  kind: "duration" | "travel",
  enabled: boolean
): string => {
  const source = current ?? "manual";
  const flags = {
    duration: source.includes("duration"),
    travel: source.includes("travel"),
  };

  flags[kind] = enabled;

  if (flags.duration && flags.travel) {
    return "duration_travel";
  }

  if (flags.duration) {
    return "duration";
  }

  return flags.travel ? "travel" : "manual";
};

const QUANTITY_TEXT_PATTERN = /^(?:\d+(?:\.\d*)?|\.\d+)$/;

/** Labor and material quantities save rounded to 2 decimals; other categories stay as typed. */
export const roundCostRowQuantity = (
  category: WorksheetCostCategory,
  text: string
): string | null => {
  if (![WorksheetCostCategory.LABOR, WorksheetCostCategory.MATERIAL].includes(category)) {
    return null;
  }

  const value = String(text ?? "").trim();

  if (!QUANTITY_TEXT_PATTERN.test(value)) {
    return null;
  }

  return new Decimal(value).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toString();
};
